import {
  isoWeekOf,
  parseCalendarDate,
  type CalendarDate,
  type IsoWeekReference,
  type PlainDate,
} from '@complan/domain';

/**
 * The single date and week formatter every web surface uses (R18.10).
 *
 * R18.10 fixes two presentation rules for the whole application: a date is shown
 * as two-digit day, two-digit month and four-digit year in that order —
 * `dd.MM.yyyy` — and a calendar week is shown as its ISO 8601 week number (1
 * through 53). This module is the *one* place those rules live: no component,
 * grid or tooltip formats a date or a week itself. Routing every surface through
 * here is what keeps the format uniform and makes a change to it a one-line edit
 * rather than a hunt across the tree.
 *
 * The week arithmetic is not re-implemented here: it delegates to the pure ISO
 * week logic in `@complan/domain` ({@link isoWeekOf}), the same functions the
 * calendar-week grid is built from, so the formatted week number can never
 * disagree with the grid column a date falls in. No date library is pulled in;
 * the domain already owns the calendar maths.
 *
 * Every input is either a domain {@link CalendarDate} or a wire {@link PlainDate}
 * (`YYYY-MM-DD`). Both are plain calendar dates with no time or zone component,
 * so formatting is a pure string transform with no clock and no locale — the
 * `dd.MM.yyyy` order is fixed by the requirement, not by the runtime's locale.
 */

/** The ISO 8601 date separator the wire format uses; not user-facing. */
const WIRE_DATE_SEPARATOR = '-';

/** The `dd.MM.yyyy` field separator R18.10 fixes; a format token, not a label. */
const DISPLAY_DATE_SEPARATOR = '.';

/**
 * Anything the formatter accepts: a domain {@link CalendarDate}, a branded wire
 * {@link PlainDate}, or a raw `YYYY-MM-DD` string (which the wire types are, but
 * which some carriers — e.g. a milestone's `plannedDate` — expose as a plain
 * `string`). All three parse to the same calendar day.
 */
export type DateInput = CalendarDate | PlainDate | string;

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

function pad4(value: number): string {
  return value.toString().padStart(4, '0');
}

/**
 * Coerces any accepted date input to a domain {@link CalendarDate}.
 *
 * A `CalendarDate` is passed through; a `YYYY-MM-DD` wire string is parsed by the
 * domain's single parser so a malformed value fails here rather than producing a
 * silently wrong display string.
 */
function asCalendarDate(date: DateInput): CalendarDate {
  if (typeof date === 'string') {
    // A raw `YYYY-MM-DD` string or a branded PlainDate both parse the same way;
    // parsing validates, so a malformed value throws here rather than displaying
    // wrong.
    return parseCalendarDate(date);
  }
  // Already a domain CalendarDate: use it directly.
  return date;
}

/**
 * Formats a calendar date as `dd.MM.yyyy` (R18.10).
 *
 * Accepts a domain {@link CalendarDate} or a wire `YYYY-MM-DD` string. The output
 * is always two-digit day, two-digit month, four-digit year, dot-separated, in
 * that order, regardless of the runtime locale.
 */
export function formatDate(date: DateInput): string {
  const { year, month, day } = asCalendarDate(date);
  return [pad2(day), pad2(month), pad4(year)].join(DISPLAY_DATE_SEPARATOR);
}

/**
 * Formats an instant as `dd.MM.yyyy HH:mm` (R18.10).
 *
 * The companion to {@link formatDate} for the values that are a *moment* rather than
 * a calendar day — a master-data source timestamp, an audit entry, a job's finish
 * time. It takes an ISO 8601 instant string (what a `timestamptz` column serialises
 * to) rather than the `YYYY-MM-DD` {@link formatDate} accepts, which is why it
 * cannot simply delegate: `parseCalendarDate` rejects an instant, by design.
 *
 * Rendered in the viewer's own time zone, which is what a reader means by "when was
 * this last refreshed". The date half keeps R18.10's dot-separated `dd.MM.yyyy`
 * ordering explicitly rather than through a locale format, so it does not reorder to
 * `MM/dd` on a US-locale browser; the clock is 24-hour for the same reason.
 *
 * An unparseable value comes back unchanged instead of as `Invalid Date`: this is a
 * display helper for data the client did not author, and showing the raw value is
 * more useful than showing a formatting failure.
 */
export function formatDateTime(instant: string): string {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) {
    return instant;
  }
  const datePart = [
    pad2(parsed.getDate()),
    pad2(parsed.getMonth() + 1),
    pad4(parsed.getFullYear()),
  ].join(DISPLAY_DATE_SEPARATOR);
  return `${datePart} ${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
}

/**
 * Formats an inclusive date range as `dd.MM.yyyy – dd.MM.yyyy` (R18.10).
 *
 * The separator is an en dash with hair spaces, matching the range wording the
 * tooltips and detail views use; the individual dates go through {@link
 * formatDate} so the range can never format its endpoints differently from a
 * standalone date.
 */
export function formatDateRange(start: DateInput, end: DateInput): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

/**
 * The ISO 8601 week number (1–53) of a date (R18.10).
 *
 * Delegates to the domain's {@link isoWeekOf}, so the number a surface displays
 * is exactly the week the calendar-week grid assigns the date to.
 */
export function isoWeekNumber(date: DateInput): number {
  return isoWeekOf(asCalendarDate(date)).isoWeek;
}

/**
 * The ISO 8601 week reference (week-based year plus week number) of a date.
 *
 * Useful where a surface needs the ISO week-based year alongside the number —
 * the first days of January can belong to the previous ISO year's week 52 or 53.
 */
export function isoWeekReference(date: DateInput): IsoWeekReference {
  return isoWeekOf(asCalendarDate(date));
}

/**
 * Formats an ISO week number as a zero-padded `Wnn` label (e.g. `W01`, `W53`).
 *
 * This is the short axis-header form the calendar-week orientation uses. The
 * number is either a raw ISO week number or resolved from a date via {@link
 * isoWeekNumber}; the `W` prefix and two-digit padding are the presentation R18.10
 * pins for the week axis.
 */
export function formatIsoWeekLabel(week: number | DateInput): string {
  const isoWeek = typeof week === 'number' ? week : isoWeekNumber(week);
  return `W${pad2(isoWeek)}`;
}

/** Re-exported for callers that need to detect the wire separator; not user-facing. */
export { WIRE_DATE_SEPARATOR };
